import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../../../ui/badge';
import { Button } from '../../../../ui/button';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../../../lib/utils';
import { Precedent } from '../../../../../types/case-workspace';

interface PrecedentCardProps {
  precedent: Precedent;
  isExpanded: boolean;
  onToggle: () => void;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(d);
  } catch {
    return dateStr;
  }
}

const PrecedentCard: React.FC<PrecedentCardProps> = ({
  precedent,
  isExpanded,
  onToggle,
  onDecisionClick,
}) => {
  const handleDRNClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDecisionClick) {
      onDecisionClick(precedent.drn);
    }
  };
  const getOutcomeBadge = (outcome: string) => {
    const isUpheld = outcome.toLowerCase().includes('upheld') && !outcome.toLowerCase().includes('not');
    return isUpheld ? 'default' : 'secondary';
  };

  // Check if drn is a valid DRN reference (starts with DRN-)
  const isValidDRN = precedent.drn && precedent.drn.startsWith('DRN-');

  // Whether this case was LLM-analyzed (has interpretation text)
  const hasInterpretation = !!precedent.interpretation;

  // Use summary or interpretation as title if drn is not a valid DRN
  const getDisplayTitle = () => {
    if (isValidDRN) {
      return precedent.drn;
    }
    const fallbackTitle = precedent.summary ||
                         (precedent.interpretation ? precedent.interpretation.substring(0, 100) : null) ||
                         'Precedent';
    return fallbackTitle.length > 100 ? fallbackTitle.substring(0, 100) + '...' : fallbackTitle;
  };

  const displayTitle = getDisplayTitle();

  // Build FOS Explorer link with summary as search query
  const explorerSearchUrl = precedent.summary
    ? `/v3/ombudsman?tab=explorer&q=${encodeURIComponent(precedent.summary)}`
    : null;

  return (
    <div
      id={`precedent-${precedent.drn}`}
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-background transition-[border-color,box-shadow]',
        isExpanded ? 'border-primary/20 shadow-[var(--shadow-card)]' : 'hover:border-primary/30'
      )}
    >
      {/* Collapsed View */}
      <div
        className={cn('cursor-pointer p-4', isExpanded && 'bg-[var(--go-good-vibes-tint)]')}
        onClick={onToggle}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Title - Use DRN if valid, otherwise use summary */}
            {isValidDRN ? (
              onDecisionClick ? (
                <button
                  onClick={handleDRNClick}
                  className="text-sm font-semibold text-primary hover:underline cursor-pointer"
                >
                  {precedent.drn}
                </button>
              ) : (
                <a
                  href={precedent.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {precedent.drn}
                </a>
              )
            ) : (
              <div>
                <div className="text-sm font-semibold text-foreground">
                  {displayTitle}
                </div>
                {precedent.drn && !isValidDRN && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Reference: {precedent.drn}
                  </div>
                )}
              </div>
            )}

            {/* Outcome + Similarity + Date */}
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant={getOutcomeBadge(precedent.outcome)}
                className="text-xs"
              >
                {precedent.outcome}
              </Badge>
              {precedent.similarity && (
                <span className="text-xs text-muted-foreground">
                  {precedent.similarity}% similar
                </span>
              )}
              {precedent.date && (
                <span className="text-xs text-muted-foreground">
                  {formatDate(precedent.date)}
                </span>
              )}
            </div>

            {/* Preview snippet */}
            {!isExpanded && (
              <p className="mt-2 line-clamp-2 text-[13px] text-muted-foreground">
                {precedent.interpretation || precedent.summary}
              </p>
            )}
          </div>

          {/* Expand/Collapse Icon */}
          <ChevronDownIcon
            className={cn(
              "w-5 h-5 text-muted-foreground transition-transform flex-shrink-0",
              isExpanded && "transform rotate-180"
            )}
          />
        </div>
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div className="border-t border-border bg-[var(--go-spring)] px-4 pb-4">
          {hasInterpretation ? (
            <>
              <h4 className="mb-2 mt-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Why relevant
              </h4>
              <p className="mb-3 text-[13px] leading-[1.7] text-foreground">
                {precedent.interpretation}
              </p>
            </>
          ) : (
            precedent.summary && (
              <>
                <h4 className="mb-2 mt-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Summary
                </h4>
                <p className="mb-3 text-[13px] leading-[1.7] text-foreground">
                  {precedent.summary}
                </p>
              </>
            )
          )}

          {precedent.keyFactors.length > 0 && (
            <>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Key factors
              </h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {precedent.keyFactors.map((factor, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {factor}
                  </Badge>
                ))}
              </div>
            </>
          )}

          <div className="mt-3 flex items-center justify-between border-t border-[var(--go-warm-border)] pt-3">
            <p className="text-xs text-muted-foreground">
              {precedent.similarity && `Similarity ${precedent.similarity}% • `}
              {precedent.date && `${formatDate(precedent.date)} • `}
              Outcome: {precedent.outcome.toLowerCase()}
            </p>
            <div className="flex items-center gap-2">
              {explorerSearchUrl && (
                <Link
                  to={explorerSearchUrl}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MagnifyingGlassIcon className="w-3.5 h-3.5" />
                  Explore similar
                </Link>
              )}
              {precedent.url && (
                <Button variant="link" size="sm" className="text-xs h-auto p-0" asChild>
                  <a href={precedent.url} target="_blank" rel="noopener noreferrer">
                    View details
                  </a>
                </Button>
              )}
              {precedent.pdfUrl && (
                <a
                  href={precedent.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Read Full Decision
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrecedentCard;
